(function () {
  const MODAL_SELECTOR = '[data-subject-select-modal]';
  const CLOSE_SELECTOR = '[data-subject-select-modal-close]';
  const TITLE_SELECTOR = '[data-subject-select-modal-title]';
  const DESCRIPTION_SELECTOR = '[data-subject-select-modal-description]';
  const PHOTO_IMAGE_SELECTOR = '[data-subject-select-modal-photo-image]';
  const FACES_SELECTOR = '[data-subject-select-modal-faces]';
  const REUPLOAD_SELECTOR = '[data-subject-select-modal-reupload]';
  const SUBMIT_SELECTOR = '[data-subject-select-modal-submit]';
  const FACE_SELECTOR = '[data-subject-select-modal-face]';
  const MARKER_SELECTOR = '[data-subject-select-modal-marker]';
  const REVEAL_MARKER_SELECTOR = '[data-subject-select-modal-reveal-marker]';
  const EXIT_MARKER_SELECTOR = '[data-subject-select-modal-exit-marker]';
  const MARKER_HINT_SELECTOR = '[data-subject-select-modal-marker-hint]';
  // Arrow-key steps as a fraction of the frame. Coarse gets you across the
  // photo in a sane number of presses; fine lands on a small face.
  const MARKER_COARSE_STEP = 0.02;
  const MARKER_FINE_STEP = 0.005;
  const DEFAULT_TITLE = 'Select the child in the photo';
  const DEFAULT_DESCRIPTION = 'We spotted more than one face in your photo. Tap your child so every illustration looks like them.';
  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'area[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'iframe',
    'object',
    'embed',
    '[contenteditable="true"]',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  function clamp01(value) {
    return Math.min(Math.max(value, 0), 1);
  }

  class SubjectSelectModal {
    constructor(modal) {
      this.modal = modal;
      this.dialog = modal.querySelector('.subject-select-modal__dialog');
      this.closeButton = modal.querySelector('.subject-select-modal__close');
      this.titleEl = modal.querySelector(TITLE_SELECTOR);
      this.descriptionEl = modal.querySelector(DESCRIPTION_SELECTOR);
      this.photoImage = modal.querySelector(PHOTO_IMAGE_SELECTOR);
      this.facesContainer = modal.querySelector(FACES_SELECTOR);
      this.reuploadButton = modal.querySelector(REUPLOAD_SELECTOR);
      this.submitButton = modal.querySelector(SUBMIT_SELECTOR);
      // Created on demand in enterMarkerMode(), removed in exitMarkerMode() —
      // never present in the document unless the customer asked for it.
      this.marker = null;
      this.revealMarkerButton = modal.querySelector(REVEAL_MARKER_SELECTOR);
      this.exitMarkerButton = modal.querySelector(EXIT_MARKER_SELECTOR);
      this.markerHint = modal.querySelector(MARKER_HINT_SELECTOR);

      this.isOpen = false;
      this.selectedIndex = null;
      this.context = null;
      this.lastFocusedElement = null;
      this.scrollY = 0;
      this.previousBodyStyles = null;
      // Marker state. markerPlaced stays false while the marker merely sits at
      // its starting point — a marker the customer has not moved or confirmed
      // is not a choice, so it must not enable Select.
      this.markerMode = false;
      this.markerPoint = null;
      this.markerPlaced = false;
      this.markerPointerId = null;
      this.markerGrabOffset = null;

      this.handleCloseClick = this.handleCloseClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleFacesClick = this.handleFacesClick.bind(this);
      this.handleReuploadClick = this.handleReuploadClick.bind(this);
      this.handleSubmitClick = this.handleSubmitClick.bind(this);
      this.handleRevealMarkerClick = this.handleRevealMarkerClick.bind(this);
      this.handleExitMarkerClick = this.handleExitMarkerClick.bind(this);
      this.handleMarkerPointerDown = this.handleMarkerPointerDown.bind(this);
      this.handleMarkerPointerMove = this.handleMarkerPointerMove.bind(this);
      this.handleMarkerPointerUp = this.handleMarkerPointerUp.bind(this);
      this.handleMarkerKeydown = this.handleMarkerKeydown.bind(this);

      this.init();
    }

    init() {
      this.modal.hidden = true;
      this.modal.setAttribute('aria-hidden', 'true');

      this.modal.querySelectorAll(CLOSE_SELECTOR).forEach((element) => {
        element.addEventListener('click', this.handleCloseClick);
      });

      document.addEventListener('keydown', this.handleKeydown);

      if (this.facesContainer) {
        this.facesContainer.addEventListener('click', this.handleFacesClick);
        this.facesContainer.addEventListener('pointerdown', this.handleMarkerPointerDown);
      }

      if (this.reuploadButton) {
        this.reuploadButton.addEventListener('click', this.handleReuploadClick);
      }

      if (this.submitButton) {
        this.submitButton.addEventListener('click', this.handleSubmitClick);
      }

      if (this.revealMarkerButton) {
        this.revealMarkerButton.addEventListener('click', this.handleRevealMarkerClick);
      }

      if (this.exitMarkerButton) {
        this.exitMarkerButton.addEventListener('click', this.handleExitMarkerClick);
      }
    }

    destroy() {
      if (this.isOpen) {
        this.close({ restoreFocus: false });
      }

      this.modal.querySelectorAll(CLOSE_SELECTOR).forEach((element) => {
        element.removeEventListener('click', this.handleCloseClick);
      });

      document.removeEventListener('keydown', this.handleKeydown);

      if (this.facesContainer) {
        this.facesContainer.removeEventListener('click', this.handleFacesClick);
        this.facesContainer.removeEventListener('pointerdown', this.handleMarkerPointerDown);
      }

      if (this.reuploadButton) {
        this.reuploadButton.removeEventListener('click', this.handleReuploadClick);
      }

      if (this.submitButton) {
        this.submitButton.removeEventListener('click', this.handleSubmitClick);
      }

      if (this.revealMarkerButton) {
        this.revealMarkerButton.removeEventListener('click', this.handleRevealMarkerClick);
      }

      if (this.exitMarkerButton) {
        this.exitMarkerButton.removeEventListener('click', this.handleExitMarkerClick);
      }

      this.exitMarkerMode();
    }

    // options:
    //   childName  {string}  optional, interpolated into the title
    //   photo      {url}  required; width/height are accepted but unused —
    //              the layout shrink-wraps the rendered image instead, so the
    //              face overlay cannot drift from what the customer sees
    //   candidates {x, y, width, height, confidence}[]  required, normalised 0-1
    //              relative to the photo, in source order (never reordered or
    //              styled by confidence — confidence is carried through but
    //              never shown)
    //   context    {*}  optional, echoed back verbatim on select/reupload events
    open(options) {
      options = options || {};

      if (!options.photo || !options.photo.url || !Array.isArray(options.candidates)) {
        return;
      }

      this.context = options.context || null;
      this.candidates = options.candidates;
      this.selectedIndex = null;
      // Restores nothing: no marker position survives from a previous file or
      // a previous opening of the chooser.
      this.exitMarkerMode();

      if (this.titleEl) {
        this.titleEl.textContent = options.childName
          ? `Select ${options.childName} on the photo`
          : DEFAULT_TITLE;
      }

      if (this.descriptionEl) {
        this.descriptionEl.textContent = options.description || DEFAULT_DESCRIPTION;
      }

      if (this.photoImage) {
        this.photoImage.src = options.photo.url;
        this.photoImage.alt = options.childName
          ? `Photo with ${options.childName} to select`
          : 'Photo with more than one face to select from';
      }

      this.renderFaces(options.candidates);
      this.updateSubmitState();

      if (this.isOpen) {
        return;
      }

      this.lastFocusedElement = document.activeElement;
      this.isOpen = true;
      this.modal.hidden = false;
      this.modal.setAttribute('aria-hidden', 'false');
      this.modal.classList.add('is-open');
      this.lockBodyScroll();

      window.requestAnimationFrame(() => {
        // The dialog itself, not the close button: focusing a control paints a
        // focus ring on open, which reads as "dismiss is the primary action"
        // when the primary action is picking a face. Tab still reaches every
        // control from here, and trapFocus keeps it inside.
        const focusTarget = this.dialog || this.closeButton;

        if (focusTarget) {
          focusTarget.focus({ preventScroll: true });
        }
      });
    }

    close(closeOptions) {
      closeOptions = closeOptions || {};
      const restoreFocus = closeOptions.restoreFocus !== false;

      if (!this.isOpen) {
        return;
      }

      this.isOpen = false;
      // Every close path — dismiss, reupload, submit, Escape, destroy — must
      // leave no marker behind. Without this, dismissing while the marker was
      // showing left the element sitting inside the hidden dialog, which
      // breaks "no marker in the document unless the customer asked for one".
      this.exitMarkerMode();
      this.modal.classList.remove('is-open');
      this.modal.setAttribute('aria-hidden', 'true');
      this.modal.hidden = true;
      this.unlockBodyScroll();

      if (restoreFocus && this.lastFocusedElement && document.contains(this.lastFocusedElement)) {
        this.lastFocusedElement.focus({ preventScroll: true });
      }

      this.lastFocusedElement = null;
    }

    renderFaces(candidates) {
      if (!this.facesContainer) {
        return;
      }

      this.facesContainer.textContent = '';

      candidates.forEach((box, position) => {
        if (!box) {
          return;
        }

        const face = document.createElement('button');
        face.type = 'button';
        face.className = 'subject-select-modal__face';
        face.setAttribute('data-subject-select-modal-face', '');
        face.setAttribute('data-subject-select-index', String(position));
        face.setAttribute('aria-pressed', 'false');
        face.setAttribute('aria-label', `Child ${position + 1}`);
        face.style.left = `${box.x * 100}%`;
        face.style.top = `${box.y * 100}%`;
        face.style.width = `${box.width * 100}%`;
        face.style.height = `${box.height * 100}%`;

        const check = document.createElement('img');
        check.className = 'subject-select-modal__face-check';
        check.src = this.facesContainer.dataset.checkIconUrl || '';
        check.alt = '';
        check.setAttribute('aria-hidden', 'true');
        face.appendChild(check);

        this.facesContainer.appendChild(face);
      });
    }

    updateSubmitState() {
      if (this.submitButton) {
        this.submitButton.disabled = this.selectedIndex === null && !this.markerPlaced;
      }
    }

    /* --- marker mode ------------------------------------------------------ */

    handleRevealMarkerClick(event) {
      event.preventDefault();
      this.enterMarkerMode();
    }

    handleExitMarkerClick(event) {
      event.preventDefault();
      // Back to face selection. exitMarkerMode() also clears the marker, so no
      // hint survives the switch.
      this.exitMarkerMode();
      this.updateSubmitState();

      if (this.revealMarkerButton) {
        this.revealMarkerButton.focus({ preventScroll: true });
      }
    }

    enterMarkerMode() {
      if (!this.facesContainer || this.markerMode) {
        return;
      }

      this.markerMode = true;
      // Starts at the centre of the frame, never on a detected face: starting
      // on the largest or most confident face would be the same defect as
      // auto-selecting one. markerPlaced stays false, so Select stays disabled
      // until the customer moves or confirms it.
      this.markerPoint = { x: 0.5, y: 0.5 };
      this.markerPlaced = false;
      this.markerGrabOffset = null;

      this.facesContainer.classList.add('is-marker-mode');

      const marker = document.createElement('button');
      marker.type = 'button';
      marker.className = 'subject-select-modal__marker';
      marker.setAttribute('data-subject-select-modal-marker', '');
      marker.setAttribute(
        'aria-label',
        'Marker for your child. Drag it onto their face, or move it with the arrow keys.'
      );
      // pointermove / pointerup are bound on the window per drag, in
      // handleMarkerPointerDown — not here.
      marker.addEventListener('keydown', this.handleMarkerKeydown);

      // Same tick badge a selected face carries, so the frame reads as the
      // chosen subject rather than a floating decoration.
      const check = document.createElement('img');
      check.className = 'subject-select-modal__marker-check';
      check.src = this.facesContainer.dataset.checkIconUrl || '';
      check.alt = '';
      check.setAttribute('aria-hidden', 'true');
      marker.appendChild(check);

      this.marker = marker;
      this.facesContainer.appendChild(marker);
      this.renderMarker();

      if (this.revealMarkerButton) {
        this.revealMarkerButton.hidden = true;
      }

      if (this.exitMarkerButton) {
        this.exitMarkerButton.hidden = false;
      }

      if (this.markerHint) {
        this.markerHint.hidden = false;
      }

      this.updateSubmitState();
      this.marker.focus({ preventScroll: true });
    }

    exitMarkerMode() {
      this.markerMode = false;
      this.markerPoint = null;
      this.markerPlaced = false;
      this.releaseMarkerPointer();

      if (this.facesContainer) {
        this.facesContainer.classList.remove('is-marker-mode');
      }

      if (this.marker) {
        this.marker.removeEventListener('keydown', this.handleMarkerKeydown);
        this.marker.remove();
        this.marker = null;
      }

      // Sweep any stray marker regardless of tracked state: "no marker in the
      // document unless the customer asked for one" must hold even if state
      // and DOM ever disagree.
      if (this.facesContainer) {
        this.facesContainer.querySelectorAll(MARKER_SELECTOR).forEach((stray) => stray.remove());
      }

      if (this.revealMarkerButton) {
        this.revealMarkerButton.hidden = false;
      }

      if (this.exitMarkerButton) {
        this.exitMarkerButton.hidden = true;
      }

      if (this.markerHint) {
        this.markerHint.hidden = true;
      }
    }

    renderMarker() {
      if (!this.marker || !this.markerPoint) {
        return;
      }

      this.marker.style.left = `${this.markerPoint.x * 100}%`;
      this.marker.style.top = `${this.markerPoint.y * 100}%`;
    }

    // Reads the pointer against the overlay's own box — never the window, never
    // the modal. The overlay is inset:0 on a container shrink-wrapped to the
    // <img>, so this ratio is already the normalised frame coordinate.
    pointFromEvent(event) {
      const rect = this.facesContainer.getBoundingClientRect();

      if (!rect.width || !rect.height) {
        return null;
      }

      return {
        x: clamp01((event.clientX - rect.left) / rect.width),
        y: clamp01((event.clientY - rect.top) / rect.height)
      };
    }

    handleMarkerPointerDown(event) {
      if (!this.markerMode || !this.marker) {
        return;
      }

      // A tap on a highlighted face still selects that face — the face buttons
      // stay usable in marker mode so the customer can go back.
      if (event.target.closest(FACE_SELECTOR)) {
        return;
      }

      const point = this.pointFromEvent(event);

      if (!point) {
        return;
      }

      event.preventDefault();

      // Grabbing the frame keeps the offset between the pointer and the
      // frame's centre, so a large frame does not snap its centre under the
      // finger the instant you touch its edge. Pressing anywhere else on the
      // photo centres the frame on that point instead.
      if (event.target.closest(MARKER_SELECTOR) && this.markerPoint) {
        this.markerGrabOffset = {
          x: this.markerPoint.x - point.x,
          y: this.markerPoint.y - point.y
        };
        this.markerPlaced = true;
        this.clearFaceSelection();
        this.updateSubmitState();
      } else {
        this.markerGrabOffset = { x: 0, y: 0 };
        this.setMarkerPoint(point);
      }

      this.markerPointerId = event.pointerId;
      this.marker.classList.add('is-dragging');

      // Track on the window for the duration of the drag. This is what makes a
      // drag that leaves the photo keep working; pointer capture below is an
      // optimisation on top, never a requirement — if capture is refused the
      // drag must still track, not silently stop following the finger.
      window.addEventListener('pointermove', this.handleMarkerPointerMove);
      window.addEventListener('pointerup', this.handleMarkerPointerUp);
      window.addEventListener('pointercancel', this.handleMarkerPointerUp);

      if (this.marker.setPointerCapture) {
        try {
          this.marker.setPointerCapture(event.pointerId);
        } catch {
          // capture refused — window listeners above already cover the drag
        }
      }
    }

    handleMarkerPointerMove(event) {
      if (this.markerPointerId === null || event.pointerId !== this.markerPointerId) {
        return;
      }

      const point = this.pointFromEvent(event);

      if (!point) {
        return;
      }

      event.preventDefault();

      const offset = this.markerGrabOffset || { x: 0, y: 0 };
      this.setMarkerPoint({
        x: clamp01(point.x + offset.x),
        y: clamp01(point.y + offset.y)
      });
    }

    handleMarkerPointerUp(event) {
      if (this.markerPointerId === null || event.pointerId !== this.markerPointerId) {
        return;
      }

      this.releaseMarkerPointer();
    }

    releaseMarkerPointer() {
      window.removeEventListener('pointermove', this.handleMarkerPointerMove);
      window.removeEventListener('pointerup', this.handleMarkerPointerUp);
      window.removeEventListener('pointercancel', this.handleMarkerPointerUp);

      if (this.marker) {
        this.marker.classList.remove('is-dragging');

        if (this.markerPointerId !== null && this.marker.releasePointerCapture) {
          try {
            this.marker.releasePointerCapture(this.markerPointerId);
          } catch {
            // capture was already lost — nothing to release
          }
        }
      }

      this.markerPointerId = null;
      this.markerGrabOffset = null;
    }

    handleMarkerKeydown(event) {
      if (!this.markerMode || !this.markerPoint) {
        return;
      }

      const step = event.shiftKey ? MARKER_FINE_STEP : MARKER_COARSE_STEP;
      let dx = 0;
      let dy = 0;

      switch (event.key) {
        case 'ArrowLeft': dx = -step; break;
        case 'ArrowRight': dx = step; break;
        case 'ArrowUp': dy = -step; break;
        case 'ArrowDown': dy = step; break;
        case 'Enter':
        case ' ':
          // Confirms the marker where it stands, for a customer who is happy
          // with the starting point and never moved it.
          event.preventDefault();
          event.stopPropagation();
          this.setMarkerPoint(this.markerPoint);
          return;
        default:
          return;
      }

      // preventDefault so arrows move the marker instead of scrolling the modal.
      event.preventDefault();
      event.stopPropagation();

      this.setMarkerPoint({
        x: clamp01(this.markerPoint.x + dx),
        y: clamp01(this.markerPoint.y + dy)
      });
    }

    // The single place the marker becomes a choice. Placing it clears any face
    // selection — only one hint may ever be set.
    setMarkerPoint(point) {
      this.markerPoint = point;
      this.markerPlaced = true;
      this.clearFaceSelection();
      this.renderMarker();
      this.updateSubmitState();
    }

    clearFaceSelection() {
      if (!this.facesContainer) {
        return;
      }

      this.selectedIndex = null;
      this.facesContainer.querySelectorAll(FACE_SELECTOR).forEach((face) => {
        face.classList.remove('is-selected');
        face.setAttribute('aria-pressed', 'false');
      });
    }

    handleFacesClick(event) {
      const face = event.target.closest(FACE_SELECTOR);

      if (!face || !this.facesContainer.contains(face)) {
        return;
      }

      event.preventDefault();
      this.selectFace(face);
    }

    selectFace(selectedFace) {
      const index = Number(selectedFace.getAttribute('data-subject-select-index'));

      // Choosing a face clears the marker — only one hint may ever be set.
      // This also returns the modal to its default state, so the "My child
      // isn't here" control comes back.
      if (this.markerMode) {
        this.exitMarkerMode();
      }

      this.facesContainer.querySelectorAll(FACE_SELECTOR).forEach((face) => {
        const isSelected = face === selectedFace;
        face.classList.toggle('is-selected', isSelected);
        face.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
      });

      this.selectedIndex = index;
      this.updateSubmitState();
    }

    handleCloseClick(event) {
      event.preventDefault();

      this.modal.dispatchEvent(new CustomEvent('subjectselect:dismiss', {
        bubbles: true,
        detail: { context: this.context }
      }));

      this.close();
    }

    handleReuploadClick(event) {
      event.preventDefault();

      this.modal.dispatchEvent(new CustomEvent('subjectselect:reupload', {
        bubbles: true,
        detail: { context: this.context }
      }));

      this.close();
    }

    handleSubmitClick(event) {
      event.preventDefault();

      // Exactly one of candidate / markerPoint leaves here — the two gestures
      // are mutually exclusive by construction (selectFace clears the marker,
      // setMarkerPoint clears the face selection).
      const hasFace = this.selectedIndex !== null && Boolean(this.candidates);
      const hasMarker = this.markerPlaced && Boolean(this.markerPoint);

      if (!hasFace && !hasMarker) {
        return;
      }

      this.modal.dispatchEvent(new CustomEvent('subjectselect:select', {
        bubbles: true,
        detail: {
          candidate: hasFace ? this.candidates[this.selectedIndex] : null,
          markerPoint: hasFace ? null : this.markerPoint,
          context: this.context
        }
      }));

      this.close();
    }

    handleKeydown(event) {
      if (!this.isOpen) {
        return;
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key === 'Tab') {
        this.trapFocus(event);
      }
    }

    trapFocus(event) {
      const focusableElements = this.getFocusableElements();

      if (!focusableElements.length) {
        event.preventDefault();
        this.dialog.focus({ preventScroll: true });
        return;
      }

      const firstFocusable = focusableElements[0];
      const lastFocusable = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstFocusable) {
        event.preventDefault();
        lastFocusable.focus({ preventScroll: true });
        return;
      }

      if (!event.shiftKey && document.activeElement === lastFocusable) {
        event.preventDefault();
        firstFocusable.focus({ preventScroll: true });
      }
    }

    getFocusableElements() {
      return Array.from(this.dialog.querySelectorAll(FOCUSABLE_SELECTOR)).filter((element) => {
        return element.tabIndex >= 0 &&
          !element.hidden &&
          !element.closest('[hidden]') &&
          element.getAttribute('aria-hidden') !== 'true';
      });
    }

    lockBodyScroll() {
      const body = document.body;

      this.scrollY = window.scrollY || window.pageYOffset || 0;
      this.previousBodyStyles = {
        position: body.style.position,
        top: body.style.top,
        width: body.style.width,
        overflow: body.style.overflow
      };

      body.style.position = 'fixed';
      body.style.top = `-${this.scrollY}px`;
      body.style.width = '100%';
      body.style.overflow = 'hidden';
    }

    unlockBodyScroll() {
      if (!this.previousBodyStyles) {
        return;
      }

      const body = document.body;

      body.style.position = this.previousBodyStyles.position;
      body.style.top = this.previousBodyStyles.top;
      body.style.width = this.previousBodyStyles.width;
      body.style.overflow = this.previousBodyStyles.overflow;
      window.scrollTo(0, this.scrollY);
      this.previousBodyStyles = null;
    }
  }

  function initSubjectSelectModal() {
    if (window.subjectSelectModalInstance) {
      window.subjectSelectModalInstance.destroy();
    }

    const modal = document.querySelector(MODAL_SELECTOR);

    if (!modal) {
      window.subjectSelectModalInstance = null;
      return;
    }

    window.subjectSelectModalInstance = new SubjectSelectModal(modal);
  }

  function destroySubjectSelectModal() {
    if (!window.subjectSelectModalInstance) {
      return;
    }

    window.subjectSelectModalInstance.destroy();
    window.subjectSelectModalInstance = null;
  }

  window.initSubjectSelectModal = initSubjectSelectModal;
  window.destroySubjectSelectModal = destroySubjectSelectModal;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSubjectSelectModal, { once: true });
  } else {
    initSubjectSelectModal();
  }

  if (window.Shopify && window.Shopify.designMode && !window.subjectSelectModalEditorListenersBound) {
    document.addEventListener('shopify:section:load', () => {
      window.initSubjectSelectModal();
    });
    document.addEventListener('shopify:section:unload', () => {
      window.destroySubjectSelectModal();
    });
    window.subjectSelectModalEditorListenersBound = true;
  }
})();
